import { useEffect } from "react"
import { createFileRoute, useNavigate } from "@tanstack/react-router"

import { AppShell } from "../components/layout/app-shell"
import { QueryState } from "../components/layout/query-state"
import { byMarketplaceRevenue } from "../lib/marketplace-revenue-order"
import { useMarketplaceSummaries } from "../services/use-dashboard"
import { useRates } from "../services/use-rates"

/**
 * Entry point for the storage refresh: a pass-through to one shop's page, never
 * a page of its own — the same shape `/pnl` and the keyword tracker use, and for
 * the same reason. A chooser page would cost two clicks to reach any control,
 * and switching belongs on the page itself.
 *
 * Lands on the BIGGEST channel by revenue, the same order the switcher lists
 * (`byMarketplaceRevenue`). It may well be a shop that supports neither control:
 * capability takes a read per marketplace, so there is nothing to rank on here
 * that would land on a supported one without twelve requests. The page states
 * the unsupported case plainly, and the switcher is right there.
 */
function StorageRefreshIndexPage() {
  const summaries = useMarketplaceSummaries()
  const rates = useRates()
  const navigate = useNavigate()
  // Rates decide the order, so the landing choice must wait for them: redirecting
  // first and resorting later would drop the operator on the wrong shop — and
  // this page carries write controls.
  const ranked = rates.isLoading
    ? []
    : [...(summaries.data ?? [])].sort(byMarketplaceRevenue(rates.data))
  const landing = ranked[0]?.marketplace

  useEffect(() => {
    if (landing) {
      void navigate({
        to: "/storage-refresh/$marketplace",
        params: { marketplace: landing },
        replace: true,
      })
    }
  }, [landing, navigate])

  return (
    <AppShell
      title="Storage refresh"
      description="Push storage prices and quantities onto live marketplace offers."
    >
      <QueryState
        // Redirecting counts as still loading: rendering an empty shell in the
        // frame before the navigation lands reads as "no data".
        isLoading={summaries.isLoading || rates.isLoading || landing !== undefined}
        error={summaries.error}
        isEmpty={summaries.data?.length === 0}
        emptyMessage="No marketplace is configured yet."
      >
        {null}
      </QueryState>
    </AppShell>
  )
}

export const Route = createFileRoute("/storage-refresh/")({ component: StorageRefreshIndexPage })
